import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Lead } from '../api/types';
import { apiGet, apiPost } from '../api/client';
import { stageLabel, timeAgo, pad2 } from '../lib/format';
import styles from './PipelinePage.module.css';

const ACTIVE_STAGES = [
  'new',
  'researching',
  'outreach',
  'conversation',
  'diagnostic',
  'proposal',
  'negotiation',
  'active_project',
];
const CLOSED_STAGES = ['won', 'lost', 'dormant'];

interface PaymentStatuses {
  [leadId: string]: string;
}

function PipelineRow({
  lead,
  idx,
  paymentStatuses,
  onDragStart,
  onDragEnd,
}: {
  lead: Lead;
  idx: number;
  paymentStatuses: PaymentStatuses;
  onDragStart: (e: React.DragEvent, lead: Lead) => void;
  onDragEnd: (e: React.DragEvent) => void;
}) {
  const sector = lead.sector || '';
  const when = lead.updatedAt
    ? timeAgo(lead.updatedAt)
    : lead.createdAt
      ? timeAgo(lead.createdAt)
      : '';
  const meta: { key: string; node: React.ReactNode }[] = [];
  if (sector) meta.push({ key: 'sector', node: <span>{sector}</span> });
  if (when) meta.push({ key: 'when', node: <span>{when}</span> });
  const paymentStatus = paymentStatuses[lead.id];
  if (
    lead.stage === 'active_project' &&
    paymentStatus &&
    paymentStatus !== 'paid' &&
    paymentStatus !== 'no_invoice'
  ) {
    meta.push({
      key: 'payment',
      node: (
        <span
          className={
            paymentStatus === 'overdue'
              ? styles.paymentWarningOverdue
              : styles.paymentWarningPending
          }
        >
          {paymentStatus === 'overdue' ? 'Factura vencida' : 'Pago pendiente'}
        </span>
      ),
    });
  }

  return (
    <div
      className={styles.pipeRow}
      draggable
      data-lead-id={lead.id}
      data-stage={lead.stage}
      tabIndex={0}
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
    >
      <span className={styles.pipeRowIndex}>{pad2(idx + 1)}</span>
      <Link to={`/leads/${lead.id}`} className={styles.pipeRowName}>
        <span>{lead.companyName || 'Sin nombre'}</span>
      </Link>
      {lead.tier ? (
        <span className={styles.pipeRowTier}>
          {lead.tier.replace('T', '')}
        </span>
      ) : (
        <span className={`${styles.pipeRowTier} ${styles.pipeRowTierEmpty}`}>
          &mdash;
        </span>
      )}
      {meta.length > 0 && (
        <div className={styles.pipeRowMeta}>
          {meta.map((item, i) => (
            <span key={item.key}>
              {i > 0 && <span className={styles.sep}>·</span>}
              {item.node}
            </span>
          ))}
        </div>
      )}
      <Link
        to={`/leads/${lead.id}`}
        className={styles.pipeRowEdit}
        title="Editar"
      >
        ✎
      </Link>
    </div>
  );
}

export default function PipelinePage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [paymentStatuses, setPaymentStatuses] = useState<PaymentStatuses>({});
  const [status, setStatus] = useState<{
    loading: boolean;
    error: string | null;
    message: { text: string; type: 'success' | 'error' } | null;
  }>({ loading: true, error: null, message: null });
  const draggedIdRef = useRef<string | null>(null);
  const draggedStageRef = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadLeads = useCallback(async () => {
    try {
      const data = await apiGet('/api/leads?limit=5000') as { leads?: Lead[] };
      const loaded = Array.isArray(data.leads) ? data.leads : [];
      setLeads(loaded);
      setStatus((prev) => ({ ...prev, error: null }));
      // Load payment statuses for active_project leads with crmClientId
      const targets = loaded.filter(
        (l) => l.stage === 'active_project' && l.crmClientId,
      );
      if (targets.length > 0) {
        await Promise.all(
          targets.map(async (lead) => {
            try {
              const res = await apiGet(
                `/api/leads/${lead.id}/payment-status`,
              ) as { paymentStatus?: string };
              setPaymentStatuses((prev) => ({
                ...prev,
                [lead.id]: res.paymentStatus ?? prev[lead.id],
              }));
            } catch {
              // ignore per-lead payment status errors
            }
          }),
        );
      }
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Error al cargar pipeline',
      }));
    } finally {
      setStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadLeads();
    refreshTimerRef.current = setInterval(loadLeads, 60000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadLeads]);

  // Clear toast after 3s
  useEffect(() => {
    if (!status.message) return;
    const t = setTimeout(
      () => setStatus((prev) => ({ ...prev, message: null })),
      3000,
    );
    return () => clearTimeout(t);
  }, [status.message]);

  function groupBy(stageList: string[]) {
    const out: Record<string, Lead[]> = {};
    stageList.forEach((s) => {
      out[s] = [];
    });
    leads.forEach((l) => {
      if (out[l.stage]) out[l.stage].push(l);
    });
    Object.keys(out).forEach((s) => {
      out[s].sort((a, b) => {
        const order: Record<string, number> = { T1: 0, T2: 1, T3: 2 };
        const da = order[a.tier ?? ''] ?? 3;
        const db = order[b.tier ?? ''] ?? 3;
        if (da !== db) return da - db;
        return (b.compositeScore ?? 0) - (a.compositeScore ?? 0);
      });
    });
    return out;
  }

  const activeGroups = groupBy(ACTIVE_STAGES);
  const closedGroups = groupBy(CLOSED_STAGES);

  const activeCount = ACTIVE_STAGES.reduce(
    (sum, s) => sum + (activeGroups[s]?.length ?? 0),
    0,
  );

  function handleDragStart(e: React.DragEvent, lead: Lead) {
    draggedIdRef.current = lead.id;
    draggedStageRef.current = lead.stage;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lead.id);
    const el = e.currentTarget as HTMLElement;
    el.classList.add(styles.isDragging);
  }

  function handleDragEnd(e: React.DragEvent) {
    const el = e.currentTarget as HTMLElement;
    el.classList.remove(styles.isDragging);
    draggedIdRef.current = null;
    draggedStageRef.current = null;
    setDropTarget(null);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  }

  function handleDragEnter(e: React.DragEvent, stage: string) {
    e.preventDefault();
    setDropTarget(stage);
  }

  function handleDragLeave(e: React.DragEvent) {
    const target = e.currentTarget as HTMLElement;
    if (!target.contains(e.relatedTarget as Node | null)) {
      setDropTarget((current) => {
        const stage = target.getAttribute('data-stage');
        return current === stage ? null : current;
      });
    }
  }

  async function handleDrop(e: React.DragEvent, toStage: string) {
    e.preventDefault();
    setDropTarget(null);
    if (!draggedIdRef.current || toStage === draggedStageRef.current) return;

    try {
      await apiPost(`/api/leads/${draggedIdRef.current}/transition`, { toStage });
      setLeads((prev) =>
        prev.map((l) => (l.id === draggedIdRef.current ? { ...l, stage: toStage } : l)),
      );
      setStatus((prev) => ({
        ...prev,
        message: { text: stageLabel(toStage), type: 'success' },
      }));
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        message: {
          text: err instanceof Error ? err.message : 'Error al mover lead',
          type: 'error',
        },
      }));
    }
  }

  return (
    <section className={styles.pipeShell} aria-label="Pipeline">
      <header className={styles.pipeMasthead}>
        <div className={styles.pipeMastheadInner}>
          <p className={styles.pipeKicker}>Pipeline</p>
          <h1 className={styles.pipeHead}>Flujo comercial</h1>
          <p className={styles.pipeDeck}>
            Todos los leads activos, ordenados por stage. Arrastra una fila a
            otra columna para avanzar o retroceder.
          </p>
        </div>
        <div className={styles.pipeMastheadMeta}>
          <span className={styles.pipeCount}>
            {activeCount} lead{activeCount === 1 ? '' : 's'} activos
          </span>
        </div>
      </header>

      {status.message && (
        <div
          className={
            status.message.type === 'success'
              ? styles.toastSuccess
              : styles.toastError
          }
        >
          {status.message.text}
        </div>
      )}

      {status.loading && leads.length === 0 ? (
        <div className={styles.pipeLoading}>Cargando pipeline…</div>
      ) : status.error && leads.length === 0 ? (
        <div className={styles.pipeError}>{status.error}</div>
      ) : (
        <>
          <div className={styles.pipeBoard}>
            {ACTIVE_STAGES.map((stage) => {
              const stageLeads = activeGroups[stage] ?? [];
              return (
                <div
                  key={stage}
                  className={`${styles.pipeCol} ${dropTarget === stage ? styles.isDrop : ''}`}
                  data-stage={stage}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, stage)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage)}
                >
                  <div className={styles.pipeColHead}>
                    <div className={styles.pipeColName}>
                      {stageLabel(stage)}
                    </div>
                    <div className={styles.pipeColCount}>
                      {stageLeads.length}
                    </div>
                  </div>
                  <div className={styles.pipeColBody}>
                    {stageLeads.length === 0 ? (
                      <div className={styles.pipeEmpty}>Vacío.</div>
                    ) : (
                      stageLeads.map((l, i) => (
                        <PipelineRow
                          key={l.id}
                          lead={l}
                          idx={i}
                          paymentStatuses={paymentStatuses}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <footer className={styles.pipeClosed} aria-label="Leads cerrados">
            <span className={styles.pipeClosedLabel}>Cerrados</span>
            {CLOSED_STAGES.map((stage) => {
              const stageLeads = closedGroups[stage] ?? [];
              return (
                <button
                  key={stage}
                  type="button"
                  className={`${styles.pipeClosedItem} ${dropTarget === stage ? styles.isDrop : ''}`}
                  data-stage={stage}
                  data-drop-zone="1"
                  onClick={() => {
                    window.location.href = `/library?stage=${encodeURIComponent(stage)}`;
                  }}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, stage)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage)}
                >
                  {stageLabel(stage)} <em>{stageLeads.length}</em>
                </button>
              );
            })}
          </footer>
        </>
      )}
    </section>
  );
}
